export default {
  namespaced: true,
  state: {
    /**
     * Represents the product chart state for a specific product.
     * @typedef {Object} ProductChartState
     * @property {boolean} productChartIsLoading - Indicates if the product chart is currently loading.
     * @property {Object} productChartOptions - Configuration options for the product chart.
     * @property {string} productChartOptions.xaxis.type - The X-axis type, e.g., 'datetime'.
     * @property {string[]} productChartOptions.xaxis.categories - Categories for the X-axis.
     * @property {Object} productChartOptions.chart - Chart configuration.
     * @property {string} productChartOptions.chart.id - The chart ID.
     * @property {Object} productChartOptions.chart.toolbar - Toolbar configuration.
     * @property {boolean} productChartOptions.chart.toolbar.show - Indicates whether the chart toolbar is shown.
     * @property {any[]} productChartSeries - An array representing the product chart series.
     * @property {number[]} productChartPrices - An array of product chart prices.
     * @property {Object} productChartReferencePrice - The reference price for the product chart.
     * @property {string} productChartReferencePrice.date - The date for the reference price.
     * @property {number} productChartReferencePrice.price - The price for the reference price.
     */
    /**
     * @typedef {Object.<string, ProductChartState>} ProductChartsState
     */
    /**
     * @type {ProductChartsState}
     */
    productChart: {},
    productChartSettings: {
      ...window.advAppData.productChartSettings
    }
  },
  getters: {
    getProductChart: (state) => (productId) => {
      return state.productChart[productId]
    },
    getProductChartSettings: (state) => {
      return state.productChartSettings
    },
    getProductChartIsLoading: (state) => (productId) => {
      return state.productChart[productId]?.productChartIsLoading
    },
    getProductChartOptions: (state) => (productId) => {
      return state.productChart[productId]?.productChartOptions
    },
    getProductChartSeries: (state) => (productId) => {
      return state.productChart[productId]?.productChartSeries
    },
    getProductChartPrices: (state) => (productId) => {
      return state.productChart[productId]?.productChartPrices
    },
    getProductChartReferencePrice: (state) => (productId) => {
      return state.productChart[productId]?.productChartReferencePrice
    },
    getProductChartAlreadyFetched: (state) => (productId) => {
      return state.productChart[productId] &&
        state.productChart[productId].productChartSeries &&
        state.productChart[productId].productChartSeries.length > 0 &&
        state.productChart[productId].productChartOptions && state.productChart[productId].productChartOptions.xaxis?.categories?.length > 0
    },
    getProductChartInitialData: (state, getters, rootState) => (productId) => {
      const initialProducts = rootState.products
      if (!initialProducts) {
        return false
      }
      const product = initialProducts.find(item => Number(item.productId) === Number(productId))
      return !!(product?.priceTracking.prices && product?.priceTracking.prices.length > 0)
    },
    getProductExistingChart: (state, getters, rootState) => (productId) => {
      const initialProducts = rootState.products
      if (!initialProducts) {
        return {}
      }
      const product = initialProducts.find(item => Number(item.productId) === Number(productId))
      return product?.priceTracking
    }
  },
  actions: {
    setProductChartIsLoading ({ commit }, { productId, isLoading }) {
      commit('setProductChartIsLoading', { productId, isLoading })
    },
    setProductChartOptions ({ commit }, { productId, options }) {
      commit('setProductChartOptions', { productId, options })
    },
    setProductChartSeries ({ commit }, { productId, series }) {
      commit('setProductChartSeries', { productId, series })
    },
    setProductChartPrices ({ commit }, { productId, prices }) {
      commit('setProductChartPrices', { productId, prices })
    },
    setProductChartReferencePrice ({ commit }, { productId, referencePrice }) {
      commit('setProductChartReferencePrice', { productId, referencePrice })
    }
  },
  mutations: {
    setProductChart (state, { productId, chart }) {
      state.productChart[productId] = chart
    },
    setProductChartIsLoading (state, { productId, isLoading }) {
      if (!state.productChart[productId]) {
        state.productChart[productId] = {}
        state.productChart[productId].productChartIsLoading = false
      }
      state.productChart[productId].productChartIsLoading = isLoading
    },
    setProductChartOptions (state, { productId, options }) {
      if (!state.productChart[productId]) {
        state.productChart[productId] = {}
        state.productChart[productId].productChartOptions = {}
      }
      state.productChart[productId].productChartOptions = options
    },
    setProductChartSeries (state, { productId, series }) {
      if (!state.productChart[productId]) {
        state.productChart[productId] = {}
        state.productChart[productId].productChartSeries = {}
      }
      state.productChart[productId].productChartSeries = series
    },
    setProductChartPrices (state, { productId, prices }) {
      if (!state.productChart[productId]) {
        state.productChart[productId] = {}
        state.productChart[productId].productChartPrices = []
      }
      state.productChart[productId].productChartPrices = prices
    },
    setProductChartReferencePrice (state, { productId, referencePrice }) {
      if (!state.productChart[productId]) {
        state.productChart[productId] = {}
        state.productChart[productId].productChartReferencePrice = {}
      }
      state.productChart[productId].productChartReferencePrice = referencePrice
    }
  }
}
